import React, { useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { ExclamationTriangleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const LoginForm = ({ onLoginSuccess, onForgotPassword, onUseEmailCode, onCreateAccount }) => {
  const [formData, setFormData] = useState({ username: '', password: '', rememberMe: false });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginStatus, setLoginStatus] = useState('');
  const [loginFailure, setLoginFailure] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const login = useAuthStore((state) => state.login);

  // Note: License check is performed by the backend during login, not on page load

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const isColdStartLoginError = (error) => {
    if (!error) return false;
    if (!error.response) return true;
    return [502, 503, 504].includes(error.response.status);
  };

  const loginWithColdStartRetry = async () => {
    const retryDelays = [2500, 5000, 8000, 12000, 15000];
    let lastError;

    for (let attempt = 0; attempt <= retryDelays.length; attempt += 1) {
      try {
        if (attempt === 0) {
          setLoginStatus('Signing in...');
        } else {
          setLoginStatus('Starting your secure workspace. This can take a few seconds...');
        }
        return await login(formData.username, formData.password);
      } catch (error) {
        lastError = error;
        if (!isColdStartLoginError(error) || attempt === retryDelays.length) {
          throw error;
        }
        await sleep(retryDelays[attempt]);
      }
    }

    throw lastError;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoginFailure(null);
    setIsLoading(true);
    try {
      setErrors({});  // Ensure cleared
      await loginWithColdStartRetry();
      // Wait for React state to propagate before navigating
      await new Promise(resolve => setTimeout(resolve, 50));
      onLoginSuccess();
    } catch (error) {
      // Display error and stay on login page - do NOT navigate
      const errorMessage = isColdStartLoginError(error)
        ? 'We could not reach Serviti AI yet. Please retry in a few seconds.'
        : (error.response?.data?.detail || error.message || 'Sign in failed. Please try again.');
      setErrors({ general: errorMessage });
      setLoginFailure({
        title: isColdStartLoginError(error) ? 'Serviti AI is still starting' : 'Sign in failed',
        message: errorMessage,
      });
      // Do not call onLoginSuccess() when there's an error
    } finally {
      setIsLoading(false);
      setLoginStatus('');
    }
  };

  return (
    <div className="space-y-7">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-950">
          Sign in to Serviti AI
        </h2>
        <p className="mt-2 text-sm leading-6 text-gray-600">
          Manage your AI Sales Agent, AI Lead CRM, support tickets, and campaign insights from one workspace.
        </p>
      </div>

      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              required
              value={formData.username}
              onChange={handleInputChange}
              className={`mt-2 appearance-none relative block w-full rounded-lg border px-3.5 py-3 ${
                errors.username ? 'border-red-300' : 'border-gray-300'
              } placeholder-gray-400 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              placeholder="you@example.com"
            />
            {errors.username && (
              <p className="mt-1 text-sm text-red-600">{errors.username}</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="mt-2 relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleInputChange}
                className={`appearance-none relative block w-full rounded-lg border px-3.5 py-3 pr-11 ${
                  errors.password ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-400 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="Enter password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center rounded-r-lg"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password}</p>
            )}
          </div>
        </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="rememberMe"
                name="rememberMe"
                type="checkbox"
                checked={formData.rememberMe}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-600">
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <button
                type="button"
                onClick={onForgotPassword}
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          {errors.general && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 relative">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-red-800">{errors.general}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    type="button"
                    onClick={() => setErrors({})}
                    className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {isLoading && loginStatus && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3" aria-live="polite">
              <p className="text-sm text-blue-800">{loginStatus}</p>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center items-center min-h-[46px] py-3 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {loginStatus || 'Signing in...'}
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </div>

          {onUseEmailCode && (
            <div className="text-center text-sm">
              <button
                type="button"
                onClick={onUseEmailCode}
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Sign in with a one-time code instead
              </button>
            </div>
          )}

          <div className="pt-1 text-center text-sm text-gray-600">
            New to Serviti AI?{' '}
            <button
              type="button"
              onClick={onCreateAccount}
              className="font-semibold text-blue-600 hover:text-blue-500"
            >
              Sign up
            </button>
          </div>
      </form>

      {loginFailure && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/60 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-failure-title"
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex gap-4">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 id="login-failure-title" className="text-lg font-semibold text-gray-950">
                  {loginFailure.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  {loginFailure.message}
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setLoginFailure(null);
                  setErrors({});
                }}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
